import { apiGet, apiPost } from "./client";

export type ReturnRow = {
  id: string;
  number: string;
  sourceType: string;
  sourceId: string;
  lifecycle: string;
  grossTotal: string;
  restock: boolean;
  settleMode: string;
  postedAt: string | null;
  updatedAt: string;
};

export type ReturnListPage = {
  returns: ReturnRow[];
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type ReturnListSort =
  | "number"
  | "grossTotal"
  | "lifecycle"
  | "sourceType";

export function listReturns(
  companyRef: string,
  opts?: {
    q?: string;
    lifecycle?: "draft" | "posted" | "void";
    limit?: number;
    cursor?: string | null;
    sourceType?: "pos_ticket" | "sales_order";
    sourceId?: string;
    sort?: ReturnListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.lifecycle) params.set("lifecycle", opts.lifecycle);
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sourceType) params.set("sourceType", opts.sourceType);
  if (opts?.sourceId) params.set("sourceId", opts.sourceId);
  if (opts?.sort && opts.sort !== "number") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<ReturnListPage>(
    `/c/${companyRef}/returns${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}

export function voidReturn(
  companyRef: string,
  returnId: string,
  body?: {
    idempotencyKey?: string;
    confirmOversell?: boolean;
    overrideRequestId?: string;
  },
) {
  return apiPost<{
    return: { id: string; number: string; lifecycle: string };
    replayed?: boolean;
  }>(`/c/${companyRef}/returns/${returnId}/void`, body ?? {});
}

export function getReturn(companyRef: string, returnId: string) {
  return apiGet<{
    return: {
      id: string;
      number: string;
      sourceType: string;
      sourceId: string;
      sourceNumber: string | null;
      lifecycle: string;
      restock: boolean;
      settleMode: string;
      netTotal: string;
      taxTotal: string;
      grossTotal: string;
      postedAt: string | null;
    };
    lines: Array<{
      id: string;
      productId: string;
      description: string | null;
      qty: string;
      unitPrice: string;
      lineNet: string;
      lineTax: string;
      lineGross: string;
      lotCode: string | null;
      serialCode: string | null;
    }>;
  }>(`/c/${companyRef}/returns/${returnId}`);
}

export function getReturnSource(
  companyRef: string,
  sourceType: "pos_ticket" | "sales_order",
  sourceId: string,
) {
  return apiGet<{
    source: {
      type: string;
      id: string;
      number: string;
      warehouseId: string | null;
      customerId?: string;
    };
    lines: Array<{
      sourceLineId: string;
      productId: string;
      productName: string;
      qtySold: string;
      qtyReturned: string;
      qtyOpen: string;
      unitPrice: string;
      lineGross: string;
      trackLot?: boolean;
      trackSerial?: boolean;
      lotCode?: string | null;
      serialCode?: string | null;
    }>;
  }>(`/c/${companyRef}/returns/source/${sourceType}/${sourceId}`);
}

export function postReturn(
  companyRef: string,
  body: {
    sourceType: "pos_ticket" | "sales_order";
    sourceId: string;
    warehouseId?: string;
    restock?: boolean;
    settleMode?: "cash_refund" | "credit";
    number?: string;
    idempotencyKey?: string;
    overrideRequestId?: string;
    confirmOverReturn?: boolean;
    lines: Array<{
      sourceLineId: string;
      qty: string;
      lotCode?: string;
      serialCode?: string;
    }>;
  },
) {
  return apiPost<{
    return: {
      id: string;
      number: string;
      grossTotal: string;
      restock: boolean;
      settleMode: string;
    };
    glError: string | null;
    overReturn?: boolean;
    replayed?: boolean;
  }>(`/c/${companyRef}/returns`, body);
}

export { listRecentPosTickets } from "./pos";
